import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/lib/auth';
import { useBranch } from '@/lib/branch';
import { StaffRail } from '@/components/layout/StaffRail';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Plus, Minus, Trash2, Search, Wallet, CreditCard, LogOut, Store, AlertCircle, Inbox, Receipt,
  HandCoins, Smartphone, MapPin, Phone, User as UserIcon, Hash, CheckCircle2, Copy, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { fmtMoney } from '@/lib/realtime';
import { usePolicy } from '@/lib/policy';
import { OrdersQueue } from '@/components/pos/OrdersQueue';

// Payment methods per order type. For delivery, customers can prepay (cash at
// counter, card, or mobile money) OR pay the driver at the door (COD).
const PAYMENT_METHODS_BY_TYPE = {
  pickup:   ['cash', 'card', 'mobile_money'],
  dine_in:  ['cash', 'card', 'mobile_money'],
  delivery: ['cash', 'card', 'cod', 'mobile_money'],
};

const PAYMENT_LABELS = {
  cash:         { label: 'Cash',           Icon: Wallet,     hint: 'Paid now at counter' },
  card:         { label: 'Card',           Icon: CreditCard, hint: 'Paid now on terminal' },
  cod:          { label: 'Pay on Delivery',Icon: HandCoins,  hint: 'Driver collects cash at door' },
  mobile_money: { label: 'Mobile Money',   Icon: Smartphone, hint: 'MTN / Orange Money' },
};

function NewOrderPanel({ branchId, onPlaced }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState('all');
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);

  const [orderType, setOrderType] = useState('pickup');
  const [tables, setTables] = useState([]);
  const [tableId, setTableId] = useState('');

  // Customer fields — required varies by order type. Delivery requires all of
  // name, phone, and full address; pickup just suggests them; dine-in is
  // anonymous by default (table is the identifier).
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [addrLine1, setAddrLine1] = useState('');
  const [addrLine2, setAddrLine2] = useState('');
  const [addrCity, setAddrCity] = useState('');
  const [addrPostal, setAddrPostal] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // post-submit dialog
  // ---- Coupon state ----
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null); // { code, discount, type, value, ... }
  const [couponChecking, setCouponChecking] = useState(false);

  useEffect(() => {
    if (!branchId) return undefined;
    const itemsParams = { branch_id: branchId };
    let cancelled = false;
    Promise.all([
      api.get('/menu/items', { params: itemsParams }),
      api.get('/menu/categories', { params: itemsParams }),
    ]).then(([i, c]) => {
      if (cancelled) return;
      setItems(i.data || []);
      setCategories(c.data || []);
    });
    api.get(`/branches/${branchId}/tables`).then((tr) => {
      if (!cancelled) setTables(tr.data || []);
    });
    return () => { cancelled = true; };
    // Parent re-mounts this component via `key={branchId}` so the effect runs
    // exactly once per mount; no stale state cleanup required.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((it) =>
      (activeCat === 'all' || it.category_id === activeCat) &&
      (!s || it.name.toLowerCase().includes(s)),
    );
  }, [items, activeCat, search]);

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const { policy } = usePolicy();
  const taxRate = policy.tax_enabled ? Number(policy.tax_rate || 0) : 0;
  const tax = +(subtotal * taxRate).toFixed(2);
  const deliveryFee = orderType === 'delivery' ? 3.99 : 0;
  const discount = +Number(coupon?.discount || 0).toFixed(2);
  const total = +Math.max(0, subtotal + tax + deliveryFee - discount).toFixed(2);

  const validateCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { toast.error('Enter a coupon code'); return; }
    if (!cart.length) { toast.error('Add items first so we can compute the discount'); return; }
    setCouponChecking(true);
    try {
      const { data } = await api.post('/coupons/validate', {
        code,
        subtotal,
        delivery_fee: deliveryFee,
        phone: customerPhone.trim() || undefined,
      });
      setCoupon({ ...data, code });
      setCouponInput(code);
      toast.success(`Coupon applied — saved ${data.discount?.toFixed(2)}`);
    } catch (e) {
      setCoupon(null);
      toast.error(e?.response?.data?.detail || 'Coupon not valid');
    } finally {
      setCouponChecking(false);
    }
  };
  const clearCoupon = () => { setCoupon(null); setCouponInput(''); };

  // Re-validate the discount when subtotal/delivery changes so the displayed
  // value stays accurate as the cashier adds items.
  useEffect(() => {
    if (!coupon) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post('/coupons/validate', {
          code: coupon.code,
          subtotal,
          delivery_fee: deliveryFee,
          phone: customerPhone.trim() || undefined,
        });
        if (!cancelled) setCoupon({ ...data, code: coupon.code });
      } catch (e) {
        if (!cancelled) {
          setCoupon(null);
          toast.error('Coupon no longer valid for this order');
        }
      }
    })();
    return () => { cancelled = true; };
    // We intentionally do NOT re-run on every keystroke of customerPhone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, deliveryFee, coupon?.code]);

  const add = (m) => setCart((p) => {
    const existing = p.find((x) => x.menu_item_id === m.id);
    if (existing) return p.map((x) => x.menu_item_id === m.id ? { ...x, quantity: x.quantity + 1 } : x);
    return [...p, { menu_item_id: m.id, name: m.name, price: m.price, quantity: 1 }];
  });
  const inc = (id, by) => setCart((p) => p.map((x) => x.menu_item_id === id ? { ...x, quantity: Math.max(0, x.quantity + by) } : x).filter((x) => x.quantity > 0));
  const remove = (id) => setCart((p) => p.filter((x) => x.menu_item_id !== id));

  // Per-type validation — returns array of missing field labels. Empty array == valid.
  const validationErrors = useMemo(() => {
    const errs = [];
    if (!cart.length) errs.push('At least one item');
    if (orderType === 'dine_in' && !tableId) errs.push('Table');
    if (orderType === 'delivery') {
      if (!customerName.trim()) errs.push('Customer name');
      if (!customerPhone.trim() || customerPhone.replace(/\D/g, '').length < 7) errs.push('Valid phone number');
      if (!addrLine1.trim()) errs.push('Street address');
      if (!addrCity.trim()) errs.push('City');
    }
    return errs;
  }, [cart, orderType, tableId, customerName, customerPhone, addrLine1, addrCity]);

  const isValid = validationErrors.length === 0;
  const availableMethods = PAYMENT_METHODS_BY_TYPE[orderType] || ['cash', 'card'];

  const resetForm = () => {
    setCart([]); setCustomerName(''); setCustomerPhone('');
    setAddrLine1(''); setAddrLine2(''); setAddrCity(''); setAddrPostal('');
    setTableId(''); setNotes('');
    setCoupon(null); setCouponInput('');
  };

  // Submit. paymentChoice is one of: 'cash' | 'card' | 'cod' | 'mobile_money'.
  // For delivery + cod, the order remains UNPAID — driver will collect at the door.
  // For mobile_money, status stays PENDING — cashier confirms via Orders Queue later.
  const place = async (paymentChoice) => {
    if (!isValid) {
      toast.error(`Missing: ${validationErrors.join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      // Backend stores payment_method as cash/card/mtn_money/orange_money. For COD
      // we send 'cash' but skip mark-paid — the existing delivery flow will mark
      // it paid + flag driver_cash_held when the PIN is confirmed at the door.
      const backendPaymentMethod =
        paymentChoice === 'cod' ? 'cash'
        : paymentChoice === 'mobile_money' ? 'mtn_money'
        : paymentChoice;

      const payload = {
        branch_id: branchId,
        order_type: orderType,
        items: cart.map((c) => ({ menu_item_id: c.menu_item_id, quantity: c.quantity })),
        payment_method: backendPaymentMethod,
        customer_name: customerName.trim() || (orderType === 'dine_in' ? 'Dine-in guest' : 'Walk-in'),
      };
      if (customerPhone.trim()) payload.customer_phone = customerPhone.trim();
      if (notes.trim()) payload.special_instructions = notes.trim();
      if (coupon?.code) payload.coupon_code = coupon.code;
      if (orderType === 'dine_in') payload.table_id = tableId;
      if (orderType === 'delivery') {
        payload.delivery_address = {
          line1: addrLine1.trim(),
          line2: addrLine2.trim() || undefined,
          city: addrCity.trim(),
          postal_code: addrPostal.trim() || undefined,
          phone: customerPhone.trim() || undefined,
        };
      }

      const { data: order } = await api.post('/orders', payload);

      // Mark paid immediately for upfront methods; leave unpaid for COD and pending for mobile money.
      if (paymentChoice === 'cash') {
        await api.post(`/payments/cash/mark-paid/${order.id}`);
      } else if (paymentChoice === 'card') {
        await api.post(`/payments/card/mark-paid/${order.id}`);
      }

      // Mobile money: mint a shareable payment link so the customer (or a
      // friend with airtime) can settle the bill from anywhere. The order
      // stays UNPAID until someone confirms via the link.
      let mmLink = null;
      if (paymentChoice === 'mobile_money') {
        try {
          const { data } = await api.post(
            `/payments/mobile-money/link/${order.id}`,
            { payer_phone_hint: customerPhone.trim() || undefined },
          );
          mmLink = data;
        } catch (e) {
          toast.error('Could not generate Mobile Money link — order placed unpaid.');
        }
      }

      // Show success dialog with PIN for delivery orders so the cashier can
      // share it verbally if SMS fails to reach the customer. We DON'T call
      // onPlaced() here — that switches the tab and would unmount this dialog.
      // Instead, onPlaced fires when the user dismisses the confirmation.
      setConfirmation({
        order,
        paymentChoice,
        mmLink,
      });
      resetForm();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to place order');
    } finally { setSubmitting(false); }
  };

  const copyPin = (pin) => {
    try {
      navigator.clipboard.writeText(pin);
      toast.success('PIN copied');
    } catch (e) { /* noop */ }
  };

  return (
    <div className="grid gap-4 p-4 md:grid-cols-[1fr_420px]">
      {/* Menu grid */}
      <div className="min-w-0">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="pos-menu-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search items…"
              className="pl-9"
            />
          </div>
          <Button size="sm" variant={activeCat === 'all' ? 'default' : 'secondary'} onClick={() => setActiveCat('all')}>All</Button>
          {categories.map((c) => (
            <Button key={c.id} size="sm" variant={activeCat === c.id ? 'default' : 'secondary'} onClick={() => setActiveCat(c.id)}>{c.name}</Button>
          ))}
        </div>
        <ScrollArea className="max-h-[78vh]">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((it) => (
              <Card key={it.id} data-testid="pos-menu-item" className="cursor-pointer transition-shadow hover:shadow-md" onClick={() => add(it)}>
                <CardContent className="p-3">
                  <h3 className="font-medium leading-tight">{it.name}</h3>
                  <p className="mt-1 text-sm tabular">{fmtMoney(it.price)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Right-hand cart / order builder */}
      <Card className="flex h-fit min-h-[78vh] flex-col">
        {/* Order type selector */}
        <div className="border-b border-border p-3">
          <p className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">Step 1 · Order type</p>
          <div className="grid grid-cols-3 gap-2">
            {['pickup', 'dine_in', 'delivery'].map((t) => (
              <Button
                key={t}
                data-testid={`pos-order-type-${t}`}
                size="sm"
                variant={orderType === t ? 'default' : 'secondary'}
                onClick={() => setOrderType(t)}
                className="capitalize"
              >
                {t.replace('_', ' ')}
              </Button>
            ))}
          </div>
        </div>

        {/* Step 2 — items (cart list) */}
        <div className="border-b border-border p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Step 2 · Items</p>
            <span className="text-[10px] text-muted-foreground">{cart.length} line{cart.length === 1 ? '' : 's'}</span>
          </div>
          {!cart.length && (
            <p className="rounded-md border border-dashed border-border py-4 text-center text-xs text-muted-foreground">
              Tap items on the left to add to this order
            </p>
          )}
          {cart.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {cart.map((i) => (
                <li key={i.menu_item_id} data-testid="pos-cart-row" className="flex items-center gap-2 rounded border border-border p-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{i.name}</p>
                    <p className="text-xs text-muted-foreground">{fmtMoney(i.price)} × {i.quantity}</p>
                  </div>
                  <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => inc(i.menu_item_id, -1)}><Minus className="h-3 w-3" /></Button>
                  <span className="w-5 text-center text-sm tabular">{i.quantity}</span>
                  <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => inc(i.menu_item_id, 1)}><Plus className="h-3 w-3" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(i.menu_item_id)}><Trash2 className="h-3 w-3" /></Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Step 3 — contextual customer / destination fields */}
        <div className="border-b border-border p-3">
          <p className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">Step 3 · Customer details</p>

          {orderType === 'dine_in' && (
            <div className="space-y-2">
              <Label className="text-xs">Table <span className="text-rose-600">*</span></Label>
              <select
                data-testid="pos-table-select"
                value={tableId}
                onChange={(e) => setTableId(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="">Choose table…</option>
                {tables.map((t) => <option key={t.id} value={t.id}>Table {t.table_number}</option>)}
              </select>
              <CustomerNameAutocomplete placeholder="Guest name (optional)" value={customerName} onChange={setCustomerName} onPickPhone={setCustomerPhone} />
            </div>
          )}

          {orderType === 'pickup' && (
            <div className="space-y-2">
              <div className="rounded-md bg-purple-50 px-2 py-1.5 text-[11px] text-purple-900">
                Name &amp; phone are required so we can call the customer when the order is ready.
              </div>
              <CustomerNameAutocomplete required placeholder="Customer name" value={customerName} onChange={setCustomerName} onPickPhone={setCustomerPhone} invalid={!customerName.trim()} />
              <FieldInput icon={Phone} type="tel" required placeholder="Phone number" value={customerPhone} onChange={setCustomerPhone} testid="pos-customer-phone-input" invalid={!customerPhone.trim() || customerPhone.replace(/\D/g, '').length < 7} />
            </div>
          )}

          {orderType === 'delivery' && (
            <div className="space-y-2">
              <div className="rounded-md bg-blue-50 px-2 py-1.5 text-[11px] text-blue-900">
                A 4-digit PIN will be auto-generated and texted to the customer. The driver enters it on delivery.
              </div>
              <CustomerNameAutocomplete required placeholder="Customer name" value={customerName} onChange={setCustomerName} onPickPhone={setCustomerPhone} invalid={!customerName.trim()} />
              <FieldInput icon={Phone} type="tel" required placeholder="Phone (for delivery PIN SMS)" value={customerPhone} onChange={setCustomerPhone} testid="pos-customer-phone-input" invalid={!customerPhone.trim() || customerPhone.replace(/\D/g, '').length < 7} />
              <FieldInput icon={MapPin} required placeholder="Street address" value={addrLine1} onChange={setAddrLine1} testid="pos-addr-line1-input" invalid={!addrLine1.trim()} />
              <FieldInput placeholder="Apt / Suite / Floor (optional)" value={addrLine2} onChange={setAddrLine2} testid="pos-addr-line2-input" />
              <div className="grid grid-cols-2 gap-2">
                <FieldInput required placeholder="City" value={addrCity} onChange={setAddrCity} testid="pos-addr-city-input" invalid={!addrCity.trim()} />
                <FieldInput placeholder="ZIP / Postal" value={addrPostal} onChange={setAddrPostal} testid="pos-addr-postal-input" />
              </div>
            </div>
          )}

          {orderType !== 'delivery' && (
            <Textarea
              data-testid="pos-notes-input"
              className="mt-2 min-h-[44px] text-sm"
              placeholder="Order notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          )}
          {orderType === 'delivery' && (
            <Textarea
              data-testid="pos-notes-input"
              className="mt-2 min-h-[44px] text-sm"
              placeholder="Delivery instructions (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          )}
        </div>

        {/* Step 3.5 — Coupon */}
        <div className="border-b border-border p-3">
          <p className="mb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">Step 4 · Discount code (optional)</p>
          <div className="flex items-center gap-2">
            <Input
              data-testid="pos-coupon-input"
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              placeholder="e.g. WELCOME10"
              className="h-9 font-mono text-sm uppercase"
              disabled={!!coupon}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); validateCoupon(); } }}
            />
            {coupon ? (
              <Button data-testid="pos-coupon-clear" size="sm" variant="ghost" onClick={clearCoupon}>
                Remove
              </Button>
            ) : (
              <Button data-testid="pos-coupon-apply" size="sm" variant="secondary" onClick={validateCoupon} disabled={couponChecking || !couponInput.trim()}>
                {couponChecking ? 'Checking…' : 'Apply'}
              </Button>
            )}
          </div>
          {coupon && (
            <div data-testid="pos-coupon-applied" className="mt-1.5 flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs text-emerald-900">
              <span><strong>{coupon.code}</strong>{coupon.reason ? ` · ${coupon.reason.replace(/_/g, ' ')}` : ''}</span>
              <span className="tabular font-semibold">−{fmtMoney(discount)}</span>
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="border-b border-border p-3">
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular">{fmtMoney(subtotal)}</span></div>
            {policy.tax_enabled && taxRate > 0 && (
              <div className="flex justify-between"><span className="text-muted-foreground">Tax ({(taxRate * 100).toFixed(2).replace(/\.00$/, '')}%)</span><span className="tabular">{fmtMoney(tax)}</span></div>
            )}
            {orderType === 'delivery' && <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span className="tabular">{fmtMoney(3.99)}</span></div>}
            {discount > 0 && (
              <div className="flex justify-between text-emerald-700"><span>Discount</span><span data-testid="pos-totals-discount" className="tabular">−{fmtMoney(discount)}</span></div>
            )}
            <div className="flex justify-between text-base font-semibold"><span>Total</span><span className="tabular">{fmtMoney(total)}</span></div>
          </div>
        </div>

        {/* Step 4 — payment & send to kitchen */}
        <div className="p-3">
          <p className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">Step 5 · Payment &amp; send to kitchen</p>
          {!isValid && (
            <div data-testid="pos-validation-hints" className="mb-2 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <span>Required before sending to kitchen: <strong>{validationErrors.join(', ')}</strong></span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {availableMethods.map((m) => {
              const meta = PAYMENT_LABELS[m];
              if (!meta) return null;
              const { Icon } = meta;
              return (
                <Button
                  key={m}
                  data-testid={`pos-pay-${m}`}
                  variant={m === 'cash' ? 'default' : m === 'cod' ? 'default' : 'secondary'}
                  className={`h-auto min-h-[56px] flex-col gap-0.5 px-2 py-2 text-xs leading-tight ${m === 'cod' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                  disabled={!isValid || submitting}
                  onClick={() => place(m)}
                >
                  <span className="flex items-center gap-1.5 text-sm font-semibold">
                    <Icon className="h-4 w-4" /> {meta.label}
                  </span>
                  <span className="text-[10px] opacity-80">{meta.hint}</span>
                </Button>
              );
            })}
          </div>
        </div>
      </Card>

      <OrderPlacedDialog
        confirmation={confirmation}
        onClose={() => {
          setConfirmation(null);
          // Now switch the parent to the Incoming Orders tab so the cashier
          // can see the new order live in the queue.
          onPlaced?.();
        }}
        onCopyPin={copyPin}
      />
    </div>
  );
}

/** Compact labelled input used by the order builder. */
function FieldInput({ icon: Icon, type = 'text', placeholder, value, onChange, testid, required, invalid }) {
  return (
    <div className="relative">
      {Icon && (
        <Icon className={`pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${invalid && required ? 'text-rose-500' : 'text-muted-foreground'}`} />
      )}
      <Input
        type={type}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={required ? `${placeholder} *` : placeholder}
        className={`h-9 text-sm ${Icon ? 'pl-8' : ''} ${invalid && required ? 'border-rose-300 focus-visible:ring-rose-400' : ''}`}
        aria-invalid={invalid && required ? 'true' : undefined}
      />
    </div>
  );
}

/** Customer name input with typeahead. Suggests existing customers (account
 * holders and walk-ins who've ordered before) so the cashier doesn't have to
 * retype contact info for returning guests. Selecting a suggestion also
 * populates the phone field via `onPickPhone` so downstream order routing
 * (SMS, marketing) ties back to the right customer. */
function CustomerNameAutocomplete({ value, onChange, onPickPhone, placeholder = 'Customer name', required, invalid, testid = 'pos-customer-name-input' }) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = (value || '').trim();
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/admin/customers/search', { params: { q, limit: 8 } });
        if (cancelled) return;
        setSuggestions(Array.isArray(data) ? data : []);
        setOpen((Array.isArray(data) ? data : []).length > 0);
      } catch (e) {
        if (!cancelled) { setSuggestions([]); setOpen(false); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220); // debounce
    return () => { cancelled = true; clearTimeout(t); };
  }, [value]);

  const pick = (s) => {
    onChange(s.name || '');
    if (s.phone && onPickPhone) onPickPhone(s.phone);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div className="relative">
      <FieldInput icon={UserIcon} placeholder={placeholder} value={value} onChange={(v) => { onChange(v); }} testid={testid} required={required} invalid={invalid} />
      {open && suggestions.length > 0 && (
        <ul data-testid="pos-customer-suggestions" className="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-auto rounded-md border border-border bg-popover shadow-lg">
          {suggestions.map((s, idx) => (
            <li key={`${s.phone || s.name}-${idx}`}>
              <button
                type="button"
                data-testid="pos-customer-suggestion"
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                className="flex w-full items-center justify-between gap-3 px-2 py-1.5 text-left text-sm hover:bg-muted"
              >
                <span className="truncate">
                  <span className="font-medium">{s.name || 'Unnamed'}</span>
                  {s.vip && <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-900">VIP</span>}
                </span>
                <span className="shrink-0 font-mono text-xs text-muted-foreground">{s.phone || '—'}</span>
              </button>
            </li>
          ))}
          {loading && <li className="px-2 py-1 text-[11px] text-muted-foreground">Searching…</li>}
        </ul>
      )}
    </div>
  );
}

/** Confirmation dialog shown after order is placed. For delivery orders, it
 * also surfaces the 4-digit PIN so the cashier can verbally hand it off in
 * case the customer doesn't receive the SMS. */
function OrderPlacedDialog({ confirmation, onClose, onCopyPin }) {
  const { order, paymentChoice, mmLink } = confirmation || {};

  // Build the public share URL on the same origin as the frontend.
  const mmShareUrl = mmLink && mmLink.token
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/pay/mm/${mmLink.token}`
    : null;

  // Live-poll the public link endpoint so the cashier sees "PAID" as soon
  // as the customer (or whoever they forwarded the link to) confirms.
  const [linkStatus, setLinkStatus] = useState(mmLink?.status || 'pending');
  const [linkPaidInfo, setLinkPaidInfo] = useState(null);
  useEffect(() => {
    if (!mmLink || linkStatus === 'paid') return undefined;
    let live = true;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/mobile-money/pay/${mmLink.token}`);
        if (!live) return;
        if (data?.status === 'paid') {
          setLinkStatus('paid');
          setLinkPaidInfo(data);
          clearInterval(timer);
        }
      } catch (e) { /* ignore */ }
    }, 3000);
    return () => { live = false; clearInterval(timer); };
  }, [mmLink, linkStatus]);

  if (!confirmation) return null;

  const isDelivery = order.order_type === 'delivery';
  const isCod = paymentChoice === 'cod';
  const isMobile = paymentChoice === 'mobile_money';

  const copyLink = () => {
    if (!mmShareUrl) return;
    try {
      navigator.clipboard.writeText(mmShareUrl);
      toast.success('Payment link copied');
    } catch (e) { toast.error('Could not copy — long-press to select.'); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="pos-order-placed-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            Order #{order.order_number} sent to kitchen
          </DialogTitle>
          <DialogDescription>
            {isMobile && (linkStatus === 'paid'
              ? 'Mobile money payment received — order is fully paid.'
              : 'Mobile money payment is pending. Share the link below; this dialog will update automatically when it\u2019s settled.')}
            {isCod && 'Payment will be collected by the driver on delivery.'}
            {!isCod && !isMobile && 'Payment recorded.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-border bg-secondary/50 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total</span>
              <span className="tabular font-semibold">{fmtMoney(order.total, order.currency)}</span>
            </div>
            <div className="mt-1 flex justify-between text-xs">
              <span className="text-muted-foreground">Payment</span>
              <Badge variant="outline" className="text-[10px]">{(PAYMENT_LABELS[paymentChoice]?.label) || paymentChoice}</Badge>
            </div>
          </div>

          {isMobile && mmShareUrl && (
            <div data-testid="pos-mm-link-block" className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-amber-800">
                  MTN Mobile Money &middot; payment link
                </p>
                {linkStatus === 'paid' ? (
                  <Badge data-testid="pos-mm-status-paid" className="border-0 bg-emerald-600 text-[10px] text-white hover:bg-emerald-600">
                    <CheckCircle2 className="mr-1 h-3 w-3" /> PAID
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-amber-300 bg-amber-100 text-[10px] text-amber-900">
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" /> Waiting for payment
                  </Badge>
                )}
              </div>
              <div className="mt-2 flex items-stretch gap-2">
                <Input
                  data-testid="pos-mm-link-url"
                  readOnly
                  value={mmShareUrl}
                  className="h-9 flex-1 bg-white font-mono text-xs"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  data-testid="pos-mm-link-copy"
                  type="button"
                  variant="secondary"
                  className="h-9 shrink-0 px-3"
                  onClick={copyLink}
                  disabled={linkStatus === 'paid'}
                >
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                </Button>
              </div>
              <p className="mt-2 text-xs leading-snug text-amber-800">
                Share this link with the customer — or with anyone willing to pay on their behalf. They&apos;ll enter the mobile money number to be charged.
              </p>
              {linkStatus === 'paid' && linkPaidInfo && (
                <div data-testid="pos-mm-paid-detail" className="mt-2 rounded-md border border-emerald-300 bg-white p-2 text-xs">
                  <p className="font-semibold text-emerald-800">Confirmed payment</p>
                  <p className="text-muted-foreground">Paid from <span className="font-mono">{linkPaidInfo.payer_phone}</span></p>
                  <p className="text-muted-foreground">Reference <span className="font-mono">{linkPaidInfo.reference}</span></p>
                </div>
              )}
            </div>
          )}

          {isDelivery && order.delivery_verification_code && (
            <div data-testid="pos-delivery-pin-block" className="rounded-md border border-blue-200 bg-blue-50 p-3">
              <p className="text-[10px] uppercase tracking-wider text-blue-700">Delivery PIN (sent to customer via SMS)</p>
              <div className="mt-1 flex items-center gap-3">
                <Hash className="h-5 w-5 text-blue-700" />
                <p data-testid="pos-delivery-pin" className="font-mono text-3xl font-bold tracking-[0.4em] text-blue-900">
                  {order.delivery_verification_code}
                </p>
                <Button size="sm" variant="ghost" onClick={() => onCopyPin(order.delivery_verification_code)} data-testid="pos-delivery-pin-copy">
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                </Button>
              </div>
              <p className="mt-2 text-xs text-blue-800">
                The driver will ask the customer for this PIN before handing over the food. Keep handy in case the SMS doesn&apos;t arrive.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button data-testid="pos-order-placed-close" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function POS() {
  const { user, logout } = useAuth();
  const branchCtx = useBranch();
  const nav = useNavigate();
  const [tab, setTab] = useState('queue');

  // POS is a per-restaurant terminal: scope everything to the active branch.
  const branchId = branchCtx.activeId && branchCtx.activeId !== '__all__' ? branchCtx.activeId : '';

  return (
    <div className="min-h-screen bg-background">
      <StaffRail />
      <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="font-display text-lg font-semibold">POS · Orders Command Center</h1>
            <p className="text-xs text-muted-foreground">{user?.full_name} · cashier</p>
          </div>
          {branchCtx.activeBranch && (
            <div data-testid="pos-active-branch" className="hidden items-center gap-2 rounded-md border border-border bg-secondary/60 px-3 py-1.5 sm:flex">
              <Store className="h-4 w-4 text-primary" />
              <div className="leading-tight">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Operating at</p>
                <p className="text-sm font-semibold">{branchCtx.activeBranch.name}</p>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Admin shortcut is reserved for managers/owners — cashiers are
              intentionally restricted to POS/Reports/Feedback and must not
              see this entrypoint. */}
          {['admin', 'owner'].includes(user?.role) && (
            <Button data-testid="pos-admin-shortcut" size="sm" variant="secondary" onClick={() => nav('/admin')}>Admin</Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => { logout(); }}><LogOut className="mr-1 h-4 w-4" /> Sign out</Button>
        </div>
      </header>

      {branchCtx.isAllBranches && (
        <div data-testid="pos-all-branches-banner" className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900 sm:px-6">
          <AlertCircle className="h-4 w-4" />
          <span><strong>&ldquo;All branches&rdquo;</strong> isn&apos;t a valid POS scope — pick a single restaurant from the branch switcher (top right) to ring up orders.</span>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="flex h-[calc(100vh-65px)] flex-col">
        <div className="border-b border-border bg-card px-3 pt-2">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger data-testid="pos-tab-queue" value="queue" className="gap-2">
              <Inbox className="h-4 w-4" />
              Incoming Orders
            </TabsTrigger>
            <TabsTrigger data-testid="pos-tab-new" value="new" className="gap-2">
              <Receipt className="h-4 w-4" />
              New Order
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="queue" className="m-0 flex-1 overflow-hidden">
          {branchId ? (
            <OrdersQueue key={branchId} branchId={branchId} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-muted-foreground">
              <Store className="h-10 w-10 opacity-40" />
              <p className="text-sm">Select a branch from the switcher above to see incoming orders.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="new" className="m-0 flex-1 overflow-auto">
          <NewOrderPanel key={branchId || 'no-branch'} branchId={branchId} onPlaced={() => setTab('queue')} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
